/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaMoveDialog.tsx
 * FILE_ID: JOBHUB-QA-MOVE-DIALOG-001
 * PURPOSE: Choose the destination for a selection, one level up the tree.
 * WHY: No parser can know a quote's Locations belong under "Ground Floor" —
 *      that word is nowhere in the quote. This is where the user says so, and
 *      the same gesture repairs any grouping the parser got wrong.
 * LANDMINE: "+ New Level" must NOT create the parent here and move after. The
 *      backend does both in one transaction so a refused move cannot strand an
 *      empty Level; this dialog only names it.
 * RELATED: qa/services/tree_moves.py (the one-level-up rule and the write gate)
 */
import { useMemo, useState } from 'react'
import { AlertCircle, FolderPlus, Loader2 } from 'lucide-react'

export interface QaMoveTarget {
  id: string
  name: string
}

export function QaMoveDialog({
  count,
  label,
  targets,
  allowNewLevel,
  suggestions,
  newLevelPlaceholder,
  busy = false,
  error,
  onCancel,
  onMove,
}: {
  count: number
  /** Plural noun for the selection, e.g. "areas". */
  label: string
  /** Valid parents, already narrowed to one level up by the caller. */
  targets: QaMoveTarget[]
  /** Only Locations may invent their parent; a Checklist's is an existing area. */
  allowNewLevel: boolean
  suggestions?: string[]
  newLevelPlaceholder?: string
  busy?: boolean
  error?: string | null
  onCancel: () => void
  onMove: (destination: { parentId: string } | { newLevelName: string }) => void
}) {
  const [filter, setFilter] = useState('')
  const [newLevelName, setNewLevelName] = useState('')

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    return needle ? targets.filter((t) => t.name.toLowerCase().includes(needle)) : targets
  }, [targets, filter])

  return (
    <div className="mb-1 rounded-lg border border-border bg-card p-2" data-testid="qa-move-dialog">
      <p className="px-1 pb-1.5 text-[11px] font-semibold text-foreground">
        Move {count} {label} to…
      </p>

      <input
        type="text"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder="Filter destinations"
        aria-label="Filter destinations"
        data-testid="qa-move-filter"
        className="mb-1.5 h-7 w-full rounded-md border border-border bg-background px-2 text-[11px] text-foreground"
      />

      {allowNewLevel && (
        <div className="mb-1.5 flex items-center gap-1.5">
          <FolderPlus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <input
            type="text"
            value={newLevelName}
            onChange={(event) => setNewLevelName(event.target.value)}
            placeholder={newLevelPlaceholder ?? 'New level, e.g. Ground Floor'}
            aria-label="New level name"
            data-testid="qa-move-new-level"
            className="h-7 min-w-0 flex-1 rounded-md border border-dashed border-border bg-background px-2 text-[11px] text-foreground"
          />
          <button
            type="button"
            disabled={busy || !newLevelName.trim()}
            onClick={() => {
              const name = newLevelName.trim()
              const existing = targets.find((target) => target.name.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0)
              onMove(existing ? { parentId: existing.id } : { newLevelName: name })
            }}
            data-testid="qa-move-create-level"
            className="shrink-0 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            Create &amp; move
          </button>
        </div>
      )}

      {suggestions && suggestions.length > 0 ? (
        <div className="mb-1.5">
          <p className="px-1 text-[10px] uppercase text-muted-foreground">Used in other QAs</p>
          {suggestions.filter((name) => !targets.some((target) => target.name.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0)).map((name) => (
            <button
              key={name}
              type="button"
              disabled={busy}
              onClick={() => onMove({ newLevelName: name })}
              className="flex w-full rounded-md px-2 py-1.5 text-left text-[11px] hover:bg-accent"
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}
      <ul className="max-h-[30vh] overflow-auto">
        {shown.length === 0 ? (
          <li className="px-1 py-2 text-[11px] text-muted-foreground">
            {targets.length === 0 ? 'No other destination yet.' : 'Nothing matches that filter.'}
          </li>
        ) : (
          shown.map((target) => (
            <li key={target.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onMove({ parentId: target.id })}
                className="flex w-full min-w-0 items-center rounded-md px-2 py-1.5 text-left text-[11px] text-foreground hover:bg-accent disabled:opacity-50"
              >
                <span className="min-w-0 truncate">{target.name}</span>
              </button>
            </li>
          ))
        )}
      </ul>

      {error && (
        <p className="flex items-start gap-1.5 px-1 pt-1.5 text-[11px] text-destructive">
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden /> {error}
        </p>
      )}

      <div className="flex items-center gap-2 px-1 pt-1.5">
        {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden />}
        <button
          type="button"
          onClick={onCancel}
          data-testid="qa-move-cancel"
          className="ml-auto rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
